import clsx from 'clsx';
import { m, AnimatePresence, LazyMotion, domAnimation } from 'framer-motion';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';

import Button from 'components/shared/button';
import Link from 'components/shared/link';
import MainContext from 'context/main';
import translations from 'i18n';
import getFormattedId from 'utils/get-formatted-id';

import MenuLink from './menu-link';

const ANIMATION_DURATION = 0.2;
const MOTION_EASY = [0.25, 0.1, 0.25, 1];

const variants = {
  hidden: {
    opacity: 0,
    translateY: 30,
    transition: {
      duration: ANIMATION_DURATION,
      ease: MOTION_EASY,
    },
  },
  visible: {
    opacity: 1,
    translateY: 0,
    transition: {
      duration: ANIMATION_DURATION,
      ease: MOTION_EASY,
    },
  },
};

const variantsDropdownMenu = {
  hidden: {
    height: 0,
    opacity: 0,
    transition: {
      duration: ANIMATION_DURATION,
      ease: MOTION_EASY,
    },
  },
  visible: {
    height: 'auto',
    opacity: 1,
    transition: {
      duration: ANIMATION_DURATION,
      ease: MOTION_EASY,
    },
  },
};

const SubSection = ({
  locale,
  activeDropdownLabel,
  itemLabel,
  label,
  menuItem,
  subItems,
  menuIdBranch,
}) => (
  <li className="col-span-2">
    <span className="mb-3 block whitespace-nowrap text-[11px] font-medium uppercase leading-none tracking-tight-4 text-gray-8/80">
      {label}
    </span>
    <ul
      className={clsx(
        'overflow-wrap grid',
        activeDropdownLabel === translations[locale].mainMenu.features ? 'gap-y-3' : 'gap-y-[13px]',
        {
          'grid-cols-1': menuItem?.columns === 1,
          'grid-cols-2 gap-x-7': menuItem?.columns === 2,
        }
      )}
    >
      {subItems.map((link, linkIndex) => (
        <li key={linkIndex}>
          <MenuLink
            link={link}
            activeDropdownLabel={itemLabel}
            id={menuIdBranch?.childItems?.[linkIndex]?.id}
          />
        </li>
      ))}
    </ul>
  </li>
);

const MobileMenu = ({ isOpen }) => {
  const [activeDropdownLabel, setActiveDropdownLabel] = useState(null);
  const mobileMenuRef = useRef(null);

  const {
    menus: { headerMenuItems: items, headerJPMenuItems: itemsJP, headerKOMenuItems: itemsKO },
    globalFields: {
      headerLink: { title: linkText, url: linkUrl },
      headerButton: { title: buttonText, url: buttonUrl },
    },
    sharedBlocks: {
      banner: {
        sharedBlockBanner: { show },
      },
    },
    language,
  } = useContext(MainContext);

  const locale = language?.locale ? language.locale : 'en';

  const menuItems =
    {
      ja: itemsJP,
      ko: itemsKO,
      en: items,
    }[locale] || items;

  // Fallback to English if translations for current locale are not available
  const currentTranslations = translations[locale] || translations.en;

  const menuIds = useMemo(
    () =>
      menuItems.map(({ label, childItems }) => ({
        id: getFormattedId(label),
        childItems: childItems.nodes.map(({ label, childItems }) => ({
          id: getFormattedId(label),
          childItems: childItems.nodes.map(({ label, childItems: grandChildItems }) => ({
            id: getFormattedId(label),
            childItems:
              grandChildItems?.nodes?.map(({ label }) => ({
                id: getFormattedId(label),
              })) || [],
          })),
        })),
      })),
    [menuItems]
  );

  const handleDropdownOpen = (label) => {
    const container = mobileMenuRef.current;
    container.scrollTop = 0;

    if (activeDropdownLabel === label) {
      setActiveDropdownLabel(null);
      return;
    }

    setActiveDropdownLabel(label);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
  }, [isOpen]);

  return (
    <LazyMotion features={domAnimation}>
      <AnimatePresence>
        {isOpen && (
          <m.div
            className="safe-paddings fixed inset-0 z-40 hidden h-full w-full flex-col bg-white font-sans2 xl:flex"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={variants}
          >
            <nav
              className={clsx('h-full w-full overflow-x-hidden overflow-y-scroll pt-40', {
                '!pt-16': !show,
              })}
              ref={mobileMenuRef}
            >
              <div className="container-lg">
                <ul className="flex w-full flex-col text-xs">
                  {menuItems.map(
                    ({ label, path, target, childItems: { nodes: subItems } }, index) => {
                      const itemLabel = label.toLowerCase();
                      const parentHasChildItems = !!subItems?.length;

                      return (
                        <li key={index}>
                          {parentHasChildItems && (
                            <div
                              className={clsx('relative border-b border-gray-3', {
                                'pb-3': activeDropdownLabel === itemLabel,
                              })}
                            >
                              <span
                                className={clsx(
                                  'relative flex items-center py-4 text-[15px] font-normal leading-none text-black before:absolute before:right-0 before:top-[22px] before:h-0 before:w-0 before:origin-[center_2px] before:border-4 before:border-[transparent] before:border-t-black before:transition before:duration-200',
                                  {
                                    'before:-rotate-180': activeDropdownLabel === itemLabel,
                                  }
                                )}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleDropdownOpen(itemLabel)}
                                onKeyDown={() => handleDropdownOpen(itemLabel)}
                              >
                                {label}
                              </span>
                              <AnimatePresence>
                                {activeDropdownLabel === itemLabel && (
                                  <m.div
                                    className="overflow-wrap grid-gap-x grid grid-cols-1 border-t border-gray-3"
                                    initial="hidden"
                                    animate="visible"
                                    exit="hidden"
                                    variants={variantsDropdownMenu}
                                  >
                                    <ul
                                      className={clsx(
                                        'overflow-wrap grid grid-cols-2 gap-x-8 pt-4',
                                        activeDropdownLabel ===
                                          currentTranslations.mainMenu.features
                                          ? 'gap-y-[26px]'
                                          : 'gap-y-[29px]'
                                      )}
                                    >
                                      {subItems
                                        .filter((item) => !!item?.childItems.nodes.length)
                                        .map(
                                          (
                                            { label, menuItem, childItems: { nodes: subItems } },
                                            itemIndex
                                          ) => {
                                            if (label !== 'Wrapper') {
                                              return (
                                                <SubSection
                                                  locale={locale}
                                                  activeDropdownLabel={activeDropdownLabel}
                                                  itemLabel={itemLabel}
                                                  label={label}
                                                  menuItem={menuItem}
                                                  subItems={subItems}
                                                  key={label}
                                                  menuIdBranch={
                                                    menuIds[index]?.childItems?.[itemIndex]
                                                  }
                                                />
                                              );
                                            }
                                            return subItems.map(
                                              (
                                                {
                                                  label: innerLabel,
                                                  menuItem: innerMenuItem,
                                                  childItems: { nodes: grandChildItems },
                                                },
                                                subItemIndex
                                              ) => (
                                                <SubSection
                                                  locale={locale}
                                                  activeDropdownLabel={activeDropdownLabel}
                                                  itemLabel={itemLabel}
                                                  label={innerLabel}
                                                  menuItem={innerMenuItem}
                                                  subItems={grandChildItems}
                                                  key={innerLabel}
                                                  menuIdBranch={
                                                    menuIds[index]?.childItems?.[itemIndex]
                                                      ?.childItems?.[subItemIndex]
                                                  }
                                                />
                                              )
                                            );
                                          }
                                        )}
                                      {subItems
                                        .filter((item) => !item?.childItems.nodes.length)
                                        .map((link, index) => (
                                          <li className="col-span-2" key={index}>
                                            <MenuLink link={link} />
                                          </li>
                                        ))}
                                    </ul>
                                  </m.div>
                                )}
                              </AnimatePresence>
                            </div>
                          )}

                          {!parentHasChildItems && (
                            <div className="group relative border-b border-gray-3">
                              <Link
                                className="flex items-center whitespace-nowrap py-4 text-[15px] font-normal leading-none group-hover:text-primary-2"
                                to={path}
                                target={target}
                                theme="black"
                              >
                                {label}
                              </Link>
                            </div>
                          )}
                        </li>
                      );
                    }
                  )}
                </ul>
              </div>
            </nav>
            <div className="card-shadow sticky bottom-0 z-50 mt-4 bg-white">
              <div className="container-lg">
                <div className="flex items-center justify-center gap-x-8 py-4 md:gap-x-4">
                  <Link
                    className="md:w-full md:!justify-center"
                    to={linkUrl}
                    size="md"
                    theme="secondary-2"
                  >
                    {translations[locale][linkText]}
                  </Link>
                  <Button
                    className="!h-[42px] !text-[14px] md:w-full"
                    to={buttonUrl}
                    size="md"
                    theme="secondary-2"
                  >
                    {translations[locale][buttonText]}
                  </Button>
                </div>
              </div>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </LazyMotion>
  );
};

SubSection.propTypes = {
  locale: PropTypes.string.isRequired,
  activeDropdownLabel: PropTypes.string,
  itemLabel: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  menuItem: PropTypes.shape({
    columns: PropTypes.number,
  }),
  subItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      path: PropTypes.string,
      target: PropTypes.string,
    })
  ).isRequired,
  menuIdBranch: PropTypes.shape({
    id: PropTypes.string,
    childItems: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
      })
    ),
  }),
};

SubSection.defaultProps = {
  activeDropdownLabel: null,
  menuItem: null,
  menuIdBranch: null,
};

MobileMenu.propTypes = {
  isOpen: PropTypes.bool,
};

MobileMenu.defaultProps = {
  isOpen: false,
};

export default MobileMenu;
